import { joinURL } from 'ufo'
import type { RouteLocationNormalized } from 'vue-router'
import debug from '../utils/console'

const logger = debug.withTag('chunk-error-reload')

/**
 * How long a second reload is held back after the last one. A chunk that failed
 * once on a flaky connection loads on the next try. One that keeps failing is
 * broken, and reloading again would only loop.
 */
const RELOAD_TTL_MS = 60_000

/** Where the last reload is remembered, for this tab only. */
const RELOADED_AT_KEY = 'chunk-error-reload:at'

/**
 * Whether a reload is allowed now, and if so marks it. The mark is per tab and
 * not per path, because the app rewrites its own address after load (Discover
 * settles on /discover/), and a per path guard would let that through as a new
 * page. Storage that throws allows nothing, since there is then no way to stop
 * a loop.
 */
function claimReload(now: number): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT_KEY))
    if (Number.isFinite(last) && now - last < RELOAD_TTL_MS) return false
    sessionStorage.setItem(RELOADED_AT_KEY, String(now))
    return true
  } catch {
    return false
  }
}

const isCssPreloadError = (error: unknown) =>
  error instanceof Error && error.message.includes('Unable to preload CSS')

/**
 * The address a failed navigation was heading to, when it is another page.
 * Undefined means reload in place: while the page starts up, the navigation
 * under way is to the page it is already on, and assigning that address again
 * would only jump to its #fragment instead of reloading.
 */
function otherPage(
  to: RouteLocationNormalized | undefined,
  baseURL: string
): string | undefined {
  if (!to) return undefined
  const target = new URL(joinURL(baseURL, to.fullPath), window.location.href)
  const { pathname, search } = window.location
  if (target.pathname + target.search === pathname + search) return undefined
  return target.href
}

/**
 * Reloads the page when a JavaScript chunk fails to download while the page
 * starts up or moves to another route.
 *
 * Without this, a lazily loaded chunk (the page component, say) that fails in
 * those moments takes the whole page down to Nuxt's default 500 page, on a tab
 * that has nothing wrong with it. The files are there, it is the download that
 * failed (a flaky connection, a blocker, the browser dropping the request), so
 * one reload almost always brings the page back. A chunk that fails once the
 * page is up (an ad slot, say) only costs that piece, and reloading would throw
 * away what the visitor was doing, so it is left alone.
 *
 * Nuxt's own chunk reload is switched off in nuxt.config.ts
 * (`emitRouteChunkError: 'manual'`) so it cannot run beside this one and skip
 * the guard. Like Nuxt's, a navigation that fails lands the visitor on the page
 * they were going to, joined to the app base URL so Discover stays under
 * /discover. It runs before the numbered plugins, so a chunk one of them loads
 * (the ConfigCat SDK, say) is covered too.
 */
export default defineNuxtPlugin({
  name: 'chunk-error-reload',
  enforce: 'pre',
  setup(nuxtApp) {
    const router = useRouter()
    const { baseURL } = useRuntimeConfig().app

    // Whether the first page has finished rendering, hydration included.
    let started = false
    nuxtApp.hook('app:suspense:resolve', () => {
      started = true
    })

    // The route being navigated to, while a navigation is under way. Cleared
    // only by that same navigation finishing: an earlier one that a newer one
    // cancelled can still finish afterwards and must not clear the newer one.
    let navigatingTo: RouteLocationNormalized | undefined
    router.beforeEach((to) => {
      navigatingTo = to
    })
    router.afterEach((to) => {
      if (to === navigatingTo) navigatingTo = undefined
    })

    nuxtApp.hook('app:chunkError', ({ error }) => {
      // Nuxt raises this for a stylesheet that failed to preload too, and lets
      // the page carry on without it.
      if (isCssPreloadError(error)) return
      if (started && !navigatingTo) {
        logger.warn('A chunk failed to load on a running page', error)
        return
      }
      if (!claimReload(Date.now())) {
        logger.warn('A chunk failed to load again, not reloading', error)
        return
      }
      logger.warn('A chunk failed to load, reloading the page', error)
      const href = otherPage(navigatingTo, baseURL)
      if (href) {
        window.location.href = href
      } else {
        window.location.reload()
      }
    })
  },
})
